import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { OperationObserverService } from '@x/common/operation';
import { TAssociationOrigin, TrackingService } from '@x/common/tracking';
import { TEImpressionProduct } from '@x/common/tracking/event-models';
import {
  AddToCartService,
  TAddToCartOrigin,
  TAddToTarget,
} from '@x/ecommerce-shop/app/core/order/services/add-to-cart.service';
import { TAvailabilityStatus } from '@x/ecommerce-shop/app/core/product/components/variant-availability/variant-availability';
import { ProductListItemService } from '@x/ecommerce-shop/app/core/product/services/product-list-item.service';
import { IProductItem } from '@x/ecommerce-shop/app/core/product/types/product-item';
import { SearchQParamsPipe } from '@x/ecommerce-shop/app/core/search/pipes/search-q-params.pipe';
import { IShopProductItem, TShopOrderItemContext } from '@x/ecommerce/shop-client';
import { Observable } from 'rxjs';

@Component({
  selector: 'x-product-card',
  templateUrl: './product-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  preserveWhitespaces: false,
  host: { class: 'x-product-card' },
})
export class ProductCardComponent {
  @Input() set product(product: IShopProductItem) {
    this.productItem$ = this.productItemViewService.getView$(product);
  }
  @Input() addToTarget: TAddToTarget = 'MiniCart';
  @Input() associationId?: number;
  @Input() associationOrigin?: TAssociationOrigin;
  @Input() taxonId?: number;
  @Input() origin?: TAddToCartOrigin | TAssociationOrigin;
  @Input() listName?: string;

  productItem$: Observable<IProductItem>;

  @HostBinding('class.hide-actions')
  @Input()
  hideActions = false;

  addToCartControl$ = this.operationService.control(
    (args: {
      product: IShopProductItem;
      quantity: number;
      availabilityStatus: TAvailabilityStatus;
      context?: TShopOrderItemContext;
    }) => {
      const { quantity, availabilityStatus, product, context } = args;
      return this.addToCartService.addProduct(
        product,
        {
          quantity,
          availabilityStatus,
          addToTarget: this.addToTarget,
        },
        context,
      );
    },
  );

  constructor(
    private operationService: OperationObserverService,
    private addToCartService: AddToCartService,
    private productItemViewService: ProductListItemService,
    private searchQParamsPipe: SearchQParamsPipe,
    private route: ActivatedRoute,
    private trackingService: TrackingService,
  ) {}

  async addToCart(productItemView: IProductItem, quantity = 1) {
    const {
      product,
      variantAvailability: { status },
      searchContext,
    } = productItemView;

    const shopAdId = Number(this.route.snapshot.queryParams.shopAdId) || null;
    const shopAdPlacement = this.route.snapshot.queryParams.shopAdPlacement;

    await this.addToCartControl$.activate({
      product,
      quantity,
      availabilityStatus: status,
      context: {
        queryId: searchContext?.queryId,
        position: searchContext?.position,
        queryIndex: searchContext?.queryIndex,
        objectId: searchContext?.objectId,
        ...(this.taxonId && { taxonId: this.taxonId }),
        ...(this.associationId && { associationId: this.associationId }),
        ...(this.origin && { origin: this.origin }),
        ...(shopAdId && { shopAdId }),
        ...(shopAdPlacement && { shopAdPlacement }),
      },
    });
  }

  getParams(productItem: IProductItem): Params {
    let params: Params = {};
    const searchParams = this.searchQParamsPipe.transform(productItem);

    if (searchParams) {
      params = searchParams;
    }

    params = {
      ...params,
      ...(this.associationId && { associationId: this.associationId }),
      ...(this.origin && { origin: this.origin }),
      redirect: null,
    };

    return params;
  }

  isVisible(visible: boolean, productItem: IProductItem) {
    if (visible) {
      this.trackingService.sendEvent(
        new TEImpressionProduct(this.trackingService.routeOrigin, {
          product: productItem.product,
          listName: this.listName,
          taxonId: this.taxonId,
          associationId: this.associationId,
          associationOrigin: this.associationOrigin,
        }),
      );
    }
  }
}
